import { addYears, format, isValid, parse, startOfDay, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar as CalendarIcon } from 'lucide-react';
import { forwardRef, useMemo, useState } from 'react';
import { DayPicker, type Matcher } from 'react-day-picker';

import { cn } from '@/shared/lib/utils';
import { formControlBaseClassName } from '@/shared/ui/shadcn/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/shadcn/popover';

const ISO = 'yyyy-MM-dd';
const PRETTY = "d 'de' MMMM 'de' yyyy";

/**
 * Rango de años navegables por default. El `captionLayout="dropdown"`
 * de react-day-picker v9 necesita `startMonth`/`endMonth` para popular
 * los dropdowns; sin ellos los selects quedan limitados al año actual
 * y el usuario no puede saltar a meses futuros.
 *
 * El alcance hacia atrás depende de `disablePast`:
 *  - Con `disablePast` (solo futuro): basta 1 año atrás — el pasado está
 *    deshabilitado, no tiene sentido poblar el dropdown con años inertes.
 *  - Sin `disablePast` (el pasado es válido): se abren 30 años atrás para
 *    poder registrar fechas de años anteriores (p. ej. la fecha de inicio
 *    de un proyecto que arrancó hace varios años).
 *
 * Hacia adelante siempre 30 años — horizontes de planificación APP
 * (concesiones de 25 años) sin abrumar.
 */
const PAST_YEARS_SOLO_FUTURO = 1;
const PAST_YEARS_CON_PASADO = 30;
const DEFAULT_FUTURE_YEARS = 30;

function parseIso(value: string): Date | undefined {
  if (!value) return undefined;
  const parsed = parse(value, ISO, new Date());
  return isValid(parsed) ? parsed : undefined;
}

export interface DatePickerProps {
  /** Valor ISO `YYYY-MM-DD` o cadena vacía cuando no hay selección. */
  value: string;
  onChange: (next: string) => void;
  /** Disparado al cerrar el popover (compatible con `Controller.field.onBlur`). */
  onBlur?: () => void;
  id?: string;
  name?: string;
  placeholder?: string;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  disabled?: boolean;
  className?: string;
  /** Mes mínimo navegable. */
  startMonth?: Date;
  /** Mes máximo navegable. */
  endMonth?: Date;
  /**
   * Bloquea fechas anteriores a hoy (regla de negocio común para
   * planificación). Hoy inclusive sigue seleccionable.
   */
  disablePast?: boolean;
  /**
   * Bloquea fechas estrictamente anteriores al valor ISO indicado.
   * Útil para pickers de "fecha fin" que dependen de "fecha inicio":
   * pasá `notBefore={fechaInicio}` y el calendario impedirá retroceder.
   */
  notBefore?: string;
  /** Bloquea fechas estrictamente posteriores al valor ISO indicado. */
  notAfter?: string;
}

/**
 * DatePicker compartido — `react-day-picker` v9 + Popover, locale español
 * (`date-fns/locale/es`). El trigger replica el lenguaje visual de `Input`
 * (`formControlBaseClassName`) para que en un grid de formulario los
 * pickers convivan con inputs y selects sin saltos visuales.
 *
 * I/O: trabaja siempre con cadenas ISO `YYYY-MM-DD` para que encaje
 * directo con los schemas Zod del proyecto (regex `^\d{4}-\d{2}-\d{2}$`).
 * En el display muestra el formato largo en español (`"28 de mayo de 2026"`).
 *
 * El override visual del calendario vive en `globals.css` § `.rdp-root`.
 */
export const DatePicker = forwardRef<HTMLButtonElement, DatePickerProps>(function DatePicker(
  {
    value,
    onChange,
    onBlur,
    id,
    name,
    placeholder = 'Seleccione una fecha',
    ariaInvalid,
    ariaDescribedBy,
    disabled,
    className,
    startMonth,
    endMonth,
    disablePast,
    notBefore,
    notAfter,
  },
  ref,
) {
  const [open, setOpen] = useState(false);
  const selected = parseIso(value);

  // Reglas de negocio reutilizables → `disabled` matchers de DayPicker.
  // Las combinamos para que el caller exprese intención semántica
  // (`disablePast`, `notBefore`) sin construir matchers manualmente.
  const disabledMatchers = useMemo<Matcher[] | undefined>(() => {
    const matchers: Matcher[] = [];
    if (disablePast) {
      matchers.push({ before: startOfDay(new Date()) });
    }
    const notBeforeDate = notBefore ? parseIso(notBefore) : undefined;
    if (notBeforeDate) {
      matchers.push({ before: startOfDay(notBeforeDate) });
    }
    const notAfterDate = notAfter ? parseIso(notAfter) : undefined;
    if (notAfterDate) {
      matchers.push({ after: startOfDay(notAfterDate) });
    }
    return matchers.length ? matchers : undefined;
  }, [disablePast, notBefore, notAfter]);

  // Rango efectivo para los dropdowns de mes/año. Prioridad:
  //   1. Lo que el caller explicite (`startMonth` / `endMonth`).
  //   2. El rango lógico inferido de `notBefore` / `notAfter`
  //      (extendido un poco para que el usuario "vea" más allá del
  //      límite si su negocio se lo permite a futuro).
  //   3. Un rango ancho por default centrado en hoy.
  const effectiveRange = useMemo(() => {
    const today = new Date();
    const pastYears = disablePast ? PAST_YEARS_SOLO_FUTURO : PAST_YEARS_CON_PASADO;
    const fallbackStart = startOfMonth(addYears(today, -pastYears));
    const fallbackEnd = startOfMonth(addYears(today, DEFAULT_FUTURE_YEARS));
    const notBeforeDate = notBefore ? parseIso(notBefore) : undefined;
    const notAfterDate = notAfter ? parseIso(notAfter) : undefined;
    const inferredStart = notBeforeDate ? startOfMonth(notBeforeDate) : undefined;
    // Si el caller no fijó cota superior, llevamos la vista varios
    // años más allá del notBefore para no encerrar al usuario.
    const inferredEnd = notAfterDate
      ? startOfMonth(notAfterDate)
      : notBeforeDate
        ? startOfMonth(addYears(notBeforeDate, DEFAULT_FUTURE_YEARS))
        : undefined;
    return {
      start: startMonth ?? inferredStart ?? fallbackStart,
      end: endMonth ?? inferredEnd ?? fallbackEnd,
    };
  }, [startMonth, endMonth, notBefore, notAfter, disablePast]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) onBlur?.();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          ref={ref}
          type="button"
          id={id}
          name={name}
          disabled={disabled}
          aria-invalid={ariaInvalid || undefined}
          aria-describedby={ariaDescribedBy}
          aria-haspopup="dialog"
          className={cn(
            formControlBaseClassName,
            'items-center justify-between text-left font-normal',
            !selected && 'text-[#9CA3AF]',
            className,
          )}
        >
          <span className="truncate">
            {selected ? format(selected, PRETTY, { locale: es }) : placeholder}
          </span>
          <CalendarIcon aria-hidden="true" className="ml-2 size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <DayPicker
          mode="single"
          locale={es}
          weekStartsOn={1}
          captionLayout="dropdown"
          {...(selected ? { selected } : {})}
          onSelect={(date) => {
            if (date) {
              onChange(format(date, ISO));
              setOpen(false);
            }
          }}
          startMonth={effectiveRange.start}
          endMonth={effectiveRange.end}
          defaultMonth={selected ?? (notBefore ? parseIso(notBefore) : undefined) ?? new Date()}
          {...(disabledMatchers ? { disabled: disabledMatchers } : {})}
          className="p-3"
        />
      </PopoverContent>
    </Popover>
  );
});
